<script setup lang="ts">
/**
 * **节点头部**：折叠按钮 + 类型图标 + 标题（可就地改名）+ 动作 + 徽标。
 * 单独拆出来的理由和行组件一样 —— 一个 1000 行的文件里没人能一眼看完
 * 一个区块，改的时候很容易动到隔壁。
 *
 * 改名是**边打字边生效**：`@input` 直接往上抛，没有「提交」这一步 ——
 * 失焦、点别处、关页面，名字都已经生效了。Esc 是唯一的例外（明确放弃）。
 */
import { computed } from 'vue'
import NpNodeHeaderActions from './NpNodeHeaderActions.vue'
import NpNodeTitle from './NpNodeTitle.vue'
import type { GraphNodeModel, NodeDiagnostic } from '../../types/node'
import type { NodeActionDef } from '../../node/package'

const props = defineProps<{
  model: GraphNodeModel
  collapsed: boolean
  hiddenPorts: number
  diagnostics: readonly NodeDiagnostic[]
  /** 宿主给的一句话说明（「这个节点是干什么的」）—— 库不知道领域知识 */
  typeHint?: string
  /**
   * **类型图标**（24×24 stroke 路径，宿主按类型现查）。
   * 没给就回落成那个「!」—— 悬停说明两种情况都有。
   */
  typeIcon?: string
  /** 类型声明的头部动作（宿主把 id 变成"点了干什么"） */
  actions?: readonly NodeActionDef[]
}>()

const emit = defineEmits<{
  (e: 'update:collapsed', value: boolean): void
  (e: 'rename', label: string): void
  (e: 'toggle-mute', muted: boolean): void
  (e: 'toggle-hide-unused'): void
  /** 头部动作被点了（id 由类型声明，含义由宿主解释） */
  (e: 'action', id: string): void
}>()

/* ⚠️ 就地改名（显示 ↔ 输入框那套状态机）在 `NpNodeTitle.vue` 里 —— 见它的文件头 */

/**
 * 类型图标的悬停说明：名称 / 类型 id / 作用。
 * 用 `\n` 而不是 HTML —— 原生 title 只认纯文本。
 */
const typeTooltip = computed(() =>
  [
    props.model.title,
    `类型：${props.model.type}`,
    props.typeHint ? `作用：${props.typeHint}` : '',
  ]
    .filter(Boolean)
    .join('\n'),
)

/** 徽标的严重级别：取最严重的一个 */
const worstSeverity = computed(() => {
  const order = ['error', 'warning', 'info'] as const
  for (const level of order) {
    if (props.diagnostics.some((d) => d.severity === level)) return level
  }
  return null
})

const diagnosticsTitle = computed(() =>
  props.diagnostics.map((d) => d.message).join('\n'),
)
</script>

<template>
  <header class="np-node__header">
    <button
      class="np-node__caret"
      type="button"
      :aria-expanded="!props.collapsed"
      :title="props.collapsed ? '展开' : '折叠'"
      @pointerdown.stop
      @click.stop="emit('update:collapsed', !props.collapsed)"
    >
      <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path
          d="M1.5 3.2 L5 6.8 L8.5 3.2"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <!--
        类型标识：**标题左边**一个图标，悬停显示「名称 / 类型 / 作用」。
        ⚠️ 2027 从"一个感叹号"改成"图标优先、没图标才回落成 `!`"：
        图标是**类型**的信号、名字是**用户**的信号，改名之后仍认得出。
        ⚠️ 间距一律交给 header 的 `gap`，这里不要加 margin（加过一次
        `margin-right: 5px`，结果是「折叠按钮↔!」与「!↔标题」两处不一致）。
      -->
    <span class="np-node__type-icon" :class="{ 'has-icon': !!props.typeIcon }" :title="typeTooltip">
      <!--
        ⚠️⚠️ **`v-html` 是必须的**：图标串是 **SVG 内部标记**
        （`'<rect …/><path d="…"/>'`，和 `EditorMeta.icon` 同一种写法），
        不是路径数据。

        第一版写成 `<path :d="props.typeIcon" />` —— 于是 `d` 里塞进了
        `<path d="M6 5h12…"/>` 这么一串文本，**浏览器整条路径拒收**，
        表现是"图标一个都不显示"（而且不报错、不留痕）。真机截图里
        那个位置是空的，`!` 也没有 —— 因为走了"有图标"那一支。
        `verify:nodeui` 当时没抓到：它断言的是 `path 的 d === 常量`，
        两边都是同一串错数据（自证的假绿）。现在那条断言改成读 `innerHTML`，
        `verify:packages` 另有一条**格式**断言（图标串必须以 `<` 开头）。
      -->
      <svg
        v-if="props.typeIcon"
        viewBox="0 0 24 24"
        aria-hidden="true"
        v-html="props.typeIcon"
      />
      <template v-else>!</template>
    </span>
    <NpNodeTitle
      :title="props.model.title"
      :label="props.model.label"
      @rename="(next) => emit('rename', next)"
    />

    <!--
      ★ 类型声明的头部动作按钮（2027）—— 就在 M 徽标左边（用户原话：
      "静音按钮旁"）：M 是"关掉这个节点"，那一组是"对它做点什么"。
    -->
    <NpNodeHeaderActions :actions="props.actions" @action="(id) => emit('action', id)" />

    <!-- M 徽标：不只是指示，也是**开关** -->
    <button
      class="np-node__badge np-node__badge--mute"
      :class="{ 'is-active': !!props.model.muted }"
      type="button"
      :title="
        props.model.muted
          ? '已静音：输出视为 NULL，下游回退内联默认值（点击取消）'
          : '静音这个节点'
      "
      @pointerdown.stop
      @click.stop="emit('toggle-mute', !props.model.muted)"
    >
      M
    </button>

    <!--
      端口折叠标记。
      隐藏未连接的端口之后，节点看起来会「少了几行」而没有交代 ——
      用户会以为端口丢了。所以显式说出来藏了几个，顺便当开关。
    -->
    <button
      v-if="props.hiddenPorts > 0"
      class="np-node__badge np-node__badge--hidden"
      type="button"
      :title="`已隐藏 ${props.hiddenPorts} 个未连接的端口（点击显示）`"
      @pointerdown.stop
      @click.stop="emit('toggle-hide-unused')"
    >
      {{ props.hiddenPorts }}
    </button>

    <!-- 诊断徽标：悬停看详细消息 -->
    <span
      v-if="worstSeverity"
      class="np-node__issue"
      :class="`is-${worstSeverity}`"
      :title="diagnosticsTitle"
      role="status"
    >
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path
          d="M6 1.2 L11.2 10.4 H0.8 Z"
          fill="currentColor"
          fill-opacity="0.18"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linejoin="round"
        />
        <path
          d="M6 4.4 V7.4 M6 8.9 V9.1"
          stroke="currentColor"
          stroke-width="1.4"
          stroke-linecap="round"
        />
      </svg>
    </span>

    <slot name="header-extra" />
  </header>
</template>
